import { useState } from 'react';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks/useProjects';
import { ConfirmDialog } from '../common/ConfirmDialog';
import type { ApiKey } from '../../types/project';
import './ApiKeyList.css';

interface ApiKeyListProps {
  projectId: string;
}

export function ApiKeyList({ projectId }: ApiKeyListProps) {
  const { data: apiKeys, isLoading, error } = useApiKeys(projectId);
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdKeyMessage, setCreatedKeyMessage] = useState<string | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      const result = await createApiKey.mutateAsync({
        projectId,
        data: { name: newKeyName.trim() },
      });
      setCreatedKey(result.apiKey.key || null);
      setCreatedKeyMessage(result.message || null);
      setNewKeyName('');
      setShowCreateForm(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCopyKey = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevokeKey = async () => {
    if (!keyToRevoke) return;

    try {
      await revokeApiKey.mutateAsync({
        projectId,
        keyId: keyToRevoke.id,
      });
      setKeyToRevoke(null);
    } catch {
      // Error handled by mutation
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="api-key-list">
        <div className="api-key-list__loading">
          <div className="spinner" />
          <p>Loading API keys...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="api-key-list">
        <div className="api-key-list__error">
          <p>Failed to load API keys</p>
          <p className="error-detail">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="api-key-list">
      <div className="api-key-list__header">
        <div>
          <h3>API Keys</h3>
          <p className="text-muted">
            Manage API keys for authenticating SDK requests.
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={() => setShowCreateForm(true)}
          disabled={showCreateForm}
        >
          + Create API Key
        </button>
      </div>

      {/* Create Key Form */}
      {showCreateForm && (
        <div className="api-key-create-form">
          <form onSubmit={handleCreateKey}>
            <div className="form-group">
              <label htmlFor="keyName">Key Name</label>
              <input
                id="keyName"
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production Key"
                autoFocus
                required
              />
            </div>
            {createApiKey.isError && (
              <p className="form-error">
                {createApiKey.error?.message || 'Failed to create API key'}
              </p>
            )}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewKeyName('');
                }}
                disabled={createApiKey.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={!newKeyName.trim() || createApiKey.isPending}
              >
                {createApiKey.isPending ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Created Key Display */}
      {createdKey && (
        <div className="api-key-created">
          <div className="api-key-created__icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="api-key-created__content">
            <h4>API Key Created</h4>
            <p className="api-key-created__warning">
              {createdKeyMessage || "Make sure to copy your API key now. You won't be able to see it again!"}
            </p>
            <div className="api-key-created__key">
              <code>{createdKey}</code>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={handleCopyKey}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <button
            type="button"
            className="api-key-created__dismiss"
            onClick={() => setCreatedKey(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* API Keys Table */}
      {apiKeys && apiKeys.length > 0 ? (
        <table className="api-key-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key Prefix</th>
              <th>Last Used</th>
              <th>Created</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key) => (
              <tr key={key.id}>
                <td className="api-key-table__name">{key.name}</td>
                <td>
                  <code className="api-key-table__prefix">{key.key_prefix}...</code>
                </td>
                <td>{formatDate(key.last_used_at)}</td>
                <td>{formatDate(key.created_at)}</td>
                <td>{key.expires_at ? formatDate(key.expires_at) : 'Never'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => setKeyToRevoke(key)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="api-key-list__empty">
          <p>No API keys yet. Create one to get started.</p>
        </div>
      )}

      {/* Revoke Confirmation */}
      {keyToRevoke && (
        <ConfirmDialog
          title="Revoke API Key"
          message={`Are you sure you want to revoke "${keyToRevoke.name}"? This action cannot be undone and any applications using this key will stop working.`}
          confirmLabel="Revoke"
          confirmVariant="danger"
          isLoading={revokeApiKey.isPending}
          onConfirm={handleRevokeKey}
          onCancel={() => setKeyToRevoke(null)}
        />
      )}
    </div>
  );
}
